<template>
    <div v-if="modelValue">
        <div class="modal-backdrop animated-backdrop"></div>
        <div class="modal-dialog-centered modal-animated">
            <div class="modal-content">
                <div class="modal-header bg-gradient-primary">
                    <h5 class="modal-title text-white">{{ isEditMode ? 'Editar Paciente' : 'Crear Paciente' }}</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <form @submit.prevent="submitForm">
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label for="last_name" class="form-label">Apellidos</label>
                                <input
id="last_name" v-model="entity.last_name" type="text" class="form-control"
                                    :class="{ 'is-invalid': errors.last_name }">
                                <div v-if="errors.last_name" class="invalid-feedback">{{ errors.last_name[0] }}</div>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label for="first_name" class="form-label">Nombres</label>
                                <input
id="first_name" v-model="entity.first_name" type="text" class="form-control"
                                    :class="{ 'is-invalid': errors.first_name }">
                                <div v-if="errors.first_name" class="invalid-feedback">{{ errors.first_name[0] }}</div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label for="gender" class="form-label">Género</label>
                                <GenderSelect
v-model="entity.gender" :errors="errors" error-field-name="gender"
                                    placeholder="Seleccionar género" />
                            </div>
                            <div class="col-md-6 mb-3">
                                <label for="birthdate" class="form-label">Fecha de Nacimiento</label>
                                <DatePicker
v-model="entity.birthdate" :errors="errors" error-field-name="birthdate"
                                    placeholder="Seleccionar fecha de nacimiento"
                                    :max-date="new Date().toISOString().split('T')[0]" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label for="email" class="form-label">Email</label>
                                <input
id="email" v-model="entity.email" type="email" class="form-control"
                                    :class="{ 'is-invalid': errors.email }">
                                <div v-if="errors.email" class="invalid-feedback">{{ errors.email[0] }}</div>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label for="whatsapp" class="form-label">Teléfono (WhatsApp)</label>
                                <input
id="whatsapp" v-model="entity.whatsapp" type="text" class="form-control"
                                    :class="{ 'is-invalid': errors.whatsapp }">
                                <div v-if="errors.whatsapp" class="invalid-feedback">{{ errors.whatsapp[0] }}</div>
                            </div>
                        </div>
                        <div v-if="globalError" class="alert alert-danger text-white p-2">
                            <i class="fa-regular fa-circle-exclamation"></i> {{ globalError }}
                        </div>
                    </div>
                    <div class="modal-footer">
                        <argon-button color="light" variant="gradient" @click="closeModal">
                            Cancelar
                        </argon-button>
                        <argon-button color="primary" variant="gradient" type="submit">
                            {{ isEditMode ? 'Actualizar' : 'Crear' }}
                        </argon-button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, nextTick } from 'vue';
import { useStore } from 'vuex';
import ArgonButton from "@/components/ArgonButton.vue";
import GenderSelect from "@/components/MedicalRecord/Forms/Common/GenderSelect.vue";
import DatePicker from "@/components/DatePicker.vue";
import { formHandlingMixin } from "@/mixins/formHandlingMixin.js";
import '@/assets/css/modalstore.css';

const props = defineProps({
    modelValue: Boolean,
    patientData: {
        type: Object,
        default: () => ({}),
    },
    isEditMode: {
        type: Boolean,
        default: false,
    },
});

// Definir una variable global para dataStore
const dataStore = 'dataStore';
const emit = defineEmits(['update:modelValue', 'entityUpdated']);
const store = useStore();
const entity = ref({ ...props.patientData });
const errors = computed(() => store.getters[`${dataStore}/fieldErrors`] || {});
const globalError = computed(() => store.getters[`${dataStore}/error`]);

// Obtener user_id y application_id del localStorage
const getUserData = () => {
    const user = localStorage.getItem('user');
    if (user) {
        const userData = JSON.parse(user);
        return {
            user_id: userData.id,
            application_id: userData.application_id
        };
    }
    return { user_id: '', application_id: '' };
};

// Resetear formulario cuando cambie modelValue
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        const { user_id, application_id } = getUserData();
        formHandlingMixin.methods.resetForm({
            last_name: '',
            first_name: '',
            gender: '',
            birthdate: null,
            email: '',
            whatsapp: '',
            user_id: user_id,
            application_id: application_id,
        }, dataStore, props.isEditMode, props.patientData, entity, store);
    }
});

// Actualizar entidad cuando cambien los datos del paciente
watch(() => props.patientData, (newData) => {
    entity.value = { ...newData };
});

const { proxy } = getCurrentInstance();

const submitForm = async () => {
    const actionType = props.isEditMode ? "editModel" : "addModel";

    // Asegurarse de que el DOM esté actualizado
    await nextTick();
    // Seleccionar el elemento actualizado usando document.querySelector
    const element = document.querySelector(`[data-id="${entity.value.id}"]`);
    const recordName = entity.value.last_name + ' ' + entity.value.first_name;

    const result = await formHandlingMixin.methods.submitForm(
        proxy,
        entity.value,
        dataStore,
        actionType,
        element,
        recordName,
        'pacientes' // Nombre del recurso a actualizar o crear
    );

    // Si la actualización fue exitosa, emitir el evento
    if (result !== false) {
        emit('entityUpdated', entity.value);
        closeModal();
    }
};

const closeModal = () => {
    emit('update:modelValue', false);
};
</script>
